import React, { useState, useContext, useEffect, ReactNode, Suspense } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './components/sections/Home';
import About from './components/sections/About';
import Services from './components/sections/Services';
import Units from './components/sections/Units';
import Projects from './components/sections/Projects';
import Markets from './components/sections/Markets';
import Contact from './components/sections/Contact';
import Seo from './components/Seo';
import Analytics from './components/Analytics';
import UnitPage from './components/UnitPage';
import { LanguageProvider, LanguageContext, NavigationProvider, useNavigation } from './components/LanguageContext';
import { openBuildHubChat } from './components/chatEvents';

// ─── Lazy-loaded routes & heavy components ────────────────────────────────────
// These are NOT needed on the initial above-the-fold paint; splitting them out
// keeps the critical main bundle as small as possible.
const BuildHubConsole = React.lazy(() => import('./components/BuildHubConsole'));
const BlogListPage = React.lazy(() => import('./components/blog/BlogListPage'));
const BlogPostPage = React.lazy(() => import('./components/blog/BlogPostPage'));
const ChatWidget = React.lazy(() => import('./components/ChatWidget'));
const Blog = React.lazy(() => import('./components/sections/Blog'));

// ─── Suspense fallback ────────────────────────────────────────────────────────
const PageSkeleton: React.FC = () => (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-black">
        <div className="flex flex-col items-center gap-4 text-gray-400 dark:text-gray-600">
            <svg className="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm">Loading…</span>
        </div>
    </div>
);

// Slim no-op fallback for modals / widgets (they start hidden anyway)
const NullFallback: React.FC = () => null;

type OpenChat = (source?: string) => void;

const MainContent: React.FC<{ openChat: OpenChat }> = ({ openChat }) => {
    return (
        <main>
            <Home openChat={openChat} />
            <About />
            <Services openChat={openChat} />
            <Units openChat={openChat} />
            <Markets />
            <Projects openChat={openChat} />
            <Suspense fallback={<div className="min-h-24" aria-hidden="true" />}>
                <Blog />
            </Suspense>
            <Contact openChat={openChat} />
        </main>
    )
}

const AppContent: React.FC = () => {
    // 👇 NOVO: Stanje za direktno otvaranje konzole
    const [forceConsole, setForceConsole] = useState(false);
    const [chatWidgetReady, setChatWidgetReady] = useState(false);

    const { language, t } = useContext(LanguageContext);
    const { path } = useNavigation();

    // 👇 1. SUPER-SILA: Direktna provera URL-a (zaobilazi ruter)
    useEffect(() => {
        const checkUrl = () => {
            // Ako URL sadrži reč "console" bilo gde (sa tarabom ili bez), otvori konzolu
            if (window.location.href.includes('console')) {
                setForceConsole(true);
            }
        };
        checkUrl();
        window.addEventListener('popstate', checkUrl);
        window.addEventListener('hashchange', checkUrl);
        return () => {
            window.removeEventListener('popstate', checkUrl);
            window.removeEventListener('hashchange', checkUrl);
        };
    }, []);

    useEffect(() => {
        document.documentElement.lang = language;
        document.documentElement.dir = ['ar', 'fa', 'ur'].includes(language) ? 'rtl' : 'ltr';
    }, [language]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => setChatWidgetReady(true), 2500);
        return () => window.clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        if (path === '/creaton-studio') void import('./components/CreatonHero');
        if (path === '/venturelab') void import('./components/VentureLabHero');
    }, [path]);

    // 👇 2. AKO JE KONZOLA AKTIVIRANA, PRIKAŽI JE ODMAH
    if (forceConsole || path === '/console') {
        return (
            <Suspense fallback={<PageSkeleton />}>
                <BuildHubConsole />
            </Suspense>
        );
    }

    const openChat: OpenChat = (source = 'site_cta') => {
        if (!chatWidgetReady) setChatWidgetReady(true);
        openBuildHubChat(source);
    };

    const renderPage = () => {
        if (path.startsWith('/blog')) {
            const rest = path.replace(/^\/blog\/?/, '').replace(/\/+$/, '');
            if (!rest) return (
                <Suspense fallback={<PageSkeleton />}><BlogListPage /></Suspense>
            );
            const parts = rest.split('/').filter(Boolean);
            const langPart = parts.length > 1 && /^[a-z]{2}(-[A-Za-z]+)?$/i.test(parts[parts.length - 1]) ? parts.pop() : undefined;
            const slug = parts.join('/');
            // Canonical URL (no trailing slash) for crawlers & sharing
            const canonical = langPart ? `/blog/${slug}/${langPart}` : `/blog/${slug}`;
            if (path.endsWith('/') && path !== '/blog/') {
                window.history.replaceState(null, '', canonical);
            }
            return (
                <Suspense fallback={<PageSkeleton />}>
                    <BlogPostPage slug={slug} langFromPath={langPart} />
                </Suspense>
            );
        }
        const unitData = (t.data?.units || []).find((unit: any) => {
            if (!unit.href) return false;
            const normalizedUnitHref = unit.href.replace(/^\/+|\/+$/g, '');
            const normalizedPath = path.replace(/^\/+|\/+$/g, '');
            return normalizedUnitHref === normalizedPath;
        });
        if (unitData) {
            return (
                <>
                    <Seo title={unitData.seo?.title || unitData.name} description={unitData.seo?.description || unitData.description} keywords={unitData.seo?.keywords} jsonLd={[]} />
                    <UnitPage unit={unitData} openChat={openChat} />
                </>
            );
        }
        return (
            <>
                <Seo
                    title={t.seo?.home?.title}
                    description={t.seo?.home?.description}
                    keywords={t.seo?.home?.keywords || 'architecture, design, construction, engineering, buildhub, global, uae, serbia, digital twins'}
                    jsonLd={[t.seo?.organization, t.seo?.website]}
                />
                <MainContent openChat={openChat} />
            </>
        );
    };

    return (
        <div className="bg-white dark:bg-black text-gray-800 dark:text-gray-200 relative">
            <Analytics gaMeasurementId="G-3DZT9JXYFK" clarityProjectId="vf723wevr9" />
            <Header openModal={() => { }} />
            {renderPage()}
            <Footer />

            {/* 👇 3. TAJNO DUGME ZA PRISTUP (Biće u donjem levom uglu) */}
            <button
                onClick={() => setForceConsole(true)}
                className="fixed bottom-2 left-2 z-50 bg-gray-900 text-gray-500 text-xs px-2 py-1 rounded opacity-20 hover:opacity-100 transition-opacity"
                title="Admin Console"
            >
                ADMIN
            </button>

            {chatWidgetReady && (
                <Suspense fallback={<NullFallback />}>
                    <ChatWidget />
                </Suspense>
            )}
        </div>
    )
}

const App: React.FC = () => {
    return (
        <LanguageProvider>
            <NavigationProvider>
                <AppContent />
            </NavigationProvider>
        </LanguageProvider>
    );
};

export default App;
